// Dashboard.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { auth, db } from "./firebase";
import {
  collection,
  getDoc,
  getDocs,
  query,
  orderBy,
  addDoc,
  serverTimestamp,
  doc,
  onSnapshot,
  updateDoc,
  where,
  increment
} from "firebase/firestore";
import "./Dashboard.css";
import heroImg from "./assets/employpilot_hero.png";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { isIOSApp } from "./utils/platform";
import CareerProgress from "./components/CareerProgress";
import { logEvent } from "firebase/analytics";
import { analytics } from "./firebase";




function Dashboard() {
  const apiBase = process.env.REACT_APP_API_URL;

  const [formData, setFormData] = useState({
    name: "",
    role: "",
    skills: "",
    experience: "",
    education: "",
  });

  const [resume, setResume] = useState("");
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPaywall, setShowPaywall] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const ADMIN_UID = "L58LMIJJycTGSrLm1Vqrajmlapa2";
  const isAdmin = auth.currentUser?.uid === ADMIN_UID;
  const location = useLocation();



  const [selectedTemplate, setSelectedTemplate] = useState("classic");
  const [selectedVariant, setSelectedVariant] = useState("default");

  useEffect(() => {
  if (location.state?.editResume) {
    const resume = location.state.editResume;

    setResume(""); // optional clear first
    setPdfUrl(resume.pdfUrl || "");
    setSelectedTemplate(resume.template || "classic");

    // If you saved formData in Firestore:
    if (resume.formData) {
      setFormData(resume.formData);
    }
  }
}, [location.state]);


  useEffect(() => {
  const refresh = async () => {
    const user = auth.currentUser;
    if (!user) return;

    const snap = await getDoc(doc(db, "users", user.uid));
    if (!snap.exists()) return;

    const data = snap.data();
    setPlan(data.plan || "free");
    setCredits(data.credits || 0);
    setTesterPro(Boolean(data.testerPro));
  };

  refresh();
}, []);

useEffect(() => {
  const pendingResume = sessionStorage.getItem("pendingResume");
  const pendingPdf = sessionStorage.getItem("pendingPdfUrl");

  if (pendingResume && pendingPdf) {
    setResume(pendingResume);
    setPdfUrl(pendingPdf);

    sessionStorage.removeItem("pendingResume");
    sessionStorage.removeItem("pendingPdfUrl");
  }
}, []);

useEffect(() => {
  setSelectedVariant("default");
}, [selectedTemplate]);


  // Unified user model
  const [plan, setPlan] = useState("free");
  const [credits, setCredits] = useState(0);
  const [testerPro, setTesterPro] = useState(false);
  useEffect(() => {
  if (!auth.currentUser) return;

  const unsubscribe = onSnapshot(
    doc(db, "users", auth.currentUser.uid),
    (docSnap) => {
      if (!docSnap.exists()) return;

      const data = docSnap.data();

      setPlan(data?.plan || "free");
      setCredits(data?.credits || 0);
      setTesterPro(data?.isPro || false);
    }
  );

  return () => unsubscribe();
}, []);

  const paywallRequestedRef = useRef(false);
  // 🔐 Canonical entitlements (NEW)

  


  // ============================
// LOAD USER PLAN & CREDITS (Unified User Model)
// ============================


const hasProAccess =
  plan?.toLowerCase() === "pro" || isAdmin || testerPro;


useEffect(() => {
  const handler = () => setShowRules(true);
  window.addEventListener("open-community-rules", handler);
  return () => window.removeEventListener("open-community-rules", handler);
}, []);




  // ============================
  // FORM HANDLERS
  // ============================
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ============================
// LOGOUT
// ============================
const handleLogout = async () => {
  try {
    await auth.signOut();
  } catch (err) {
    console.error("Logout error:", err);
  }
};


  // ============================
  // GENERATE RESUME TEXT
  // ============================
  
  const handleGenerate = async () => {
  setLoading(true);
  try {
    const token = await auth.currentUser.getIdToken();

const res = await axios.post(
  `${apiBase}/api/generate-resume`,
  {
    ...formData,
    skills: formData.skills.split(",").map((s) => s.trim()),
    template: selectedTemplate,
    variant: selectedVariant || "default",
    userPlan: plan || "free",
  },
  {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }
);

    const generatedResume = res.data.resume || "";
const generatedPdfUrl = res.data.pdf_url || "";

setResume(generatedResume);
setPdfUrl(generatedPdfUrl);


const user = auth.currentUser;

try {

  const user = auth.currentUser;

  if (user && generatedResume) {
    await updateDoc(doc(db, "users", user.uid), {
      "progress.resumesCreated": increment(1)
    });
  }

} catch (progressErr) {
  console.error("Progress update failed:", progressErr);
}

// 🔐 Persist across redirects
sessionStorage.setItem("pendingResume", generatedResume);
sessionStorage.setItem("pendingPdfUrl", generatedPdfUrl);

// 💾 Save resume to Firestore (non-blocking safe write)
try {
  const user = auth.currentUser;

  if (user && generatedPdfUrl && plan?.toLowerCase() === "pro") {

    await addDoc(
      collection(db, "users", user.uid, "resumes"),
      {
        title: formData.role || "Untitled Resume",
        template: selectedTemplate,
        pdfUrl: generatedPdfUrl,
        createdAt: serverTimestamp(),
      }
    );
  }
} catch (saveErr) {
  console.error("Resume save failed:", saveErr);
}


  } catch (err) {
    console.error("Resume Error:", err);

    if (err.response?.status === 403) {
      setResume("🔒 Upgrade to Pro to use this resume template.");
    } else {
      setResume("⚠ Error generating resume. Please try again.");
    }
  } finally {
    setLoading(false);
  }
};


  // ============================
  // PDF DOWNLOAD LOGIC
  // ============================
const navigate = useNavigate();

const openPDF = (url) => {
  if (!url) return;

  // 🔥 TRACK REAL DOWNLOAD EVENT
  if (analytics) {
    logEvent(analytics, "pdf_downloaded", {
      source: "resume_builder"
    });
  }

  if (isIOSApp() || window.innerWidth < 768) {
    navigate("/pdf-viewer", { state: { pdfUrl: url } });
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
};


const handleDownloadPDF = async () => {
  if (!pdfUrl) return;

  // 🔐 ADMIN
  if (isAdmin) {
    openPDF(pdfUrl);
    return;
  }

  // 🔐 PRO
  if (plan?.toLowerCase() === "pro" || testerPro) {
    openPDF(pdfUrl);
    return;
  }

  // 🔐 ONE-TIME CREDIT
  if (credits > 0) {
    const newCredits = credits - 1;

    await updateDoc(doc(db, "users", auth.currentUser.uid), {
      credits: newCredits,
    });

    setCredits(newCredits);

    openPDF(pdfUrl);
    return;
  }

  // 🚫 PAYWALL (ONLY ONCE PER ATTEMPT)
  if (!paywallRequestedRef.current) {
    paywallRequestedRef.current = true;

    // 🔥 TRACK PAYWALL EVENT (SAFE)
    if (analytics) {
      logEvent(analytics, "paywall_shown", {
        context: "pdf_download"
      });
    }

    setShowPaywall(true);
  }
};





  return (
    <>
      {/* Background Image */}
      <div
        className="background-overlay"
        style={{
          backgroundImage: `url(${heroImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          pointerEvents: "none", // ✅ CRITICAL
        }}
      ></div>


      <div className="dash-wrapper">
  <div className="dash-hero"></div>

  <div className="dash-container">
    <h2 className="dash-title">Welcome to EmployPilot ✨</h2>

    <p className="dash-subtitle">
      Your AI-powered resume builder and career assistant.
    </p>

    {/* DASHBOARD GRID */}
    <div className="dashboard-layout">

      {/* LEFT COLUMN */}
      <CareerProgress />

      {/* RIGHT COLUMN */}
      <div>

        {/* FORM CARD */}
        <div className="form-card">
          <input
            type="text"
            name="name"
            placeholder="Full Name"
            onChange={handleChange}
            className="input-field"
          />

            <input
              type="text"
              name="role"
              placeholder="Job Role"
              onChange={handleChange}
              className="input-field"
            />

            <input
              type="text"
              name="skills"
              placeholder="Skills (comma separated)"
              onChange={handleChange}
              className="input-field"
            />

            <textarea
              name="experience"
              placeholder="Work Experience"
              onChange={handleChange}
              className="textarea-field"
            />

            <textarea
              name="education"
              placeholder="Education"
              onChange={handleChange}
              className="textarea-field"
            />

            <select
            className="input-field"
            value={selectedTemplate}
            onChange={(e) => {
            setSelectedTemplate(e.target.value);
            setSelectedVariant("default"); // reset variant
            }}
            >
            <option value="classic">Classic</option>
            <option value="modern">Modern</option>
            <option value="elite">Elite</option>
            <option value="executive">Executive</option>
            </select>
           

            {selectedTemplate !== "classic" && (
            <select
            className="input-field"
            value={selectedVariant}
            onChange={(e) => setSelectedVariant(e.target.value)}
            >
            <option value="default">Default Style</option>

            {selectedTemplate === "modern" && (
            <>
            <option value="modern-blue">Modern Blue</option>
            <option value="modern-purple">Modern Purple</option>
            <option value="modern-green">Modern Green</option>
            </>
            )}

            {selectedTemplate === "elite" && (
            <>
            <option value="elite-gray">Elite Gray</option>
            <option value="elite-black">Elite Black</option>
            <option value="elite-emerald">Elite Emerald</option>
            </>
            )}

            {selectedTemplate === "executive" && (
            <>
            <option value="executive-gold">Executive Gold</option>
            <option value="executive-navy">Executive Navy</option>
            <option value="executive-charcoal">Executive Charcoal</option>
            </>
            )}
            </select>
            )}

            <div style={{ marginTop: "10px" }}>
            <div
            style={{
            width: "100%",
            height: "6px",
            borderRadius: "4px",
            background:
            // MODERN (1st)
            selectedVariant === "modern-blue"
            ? "#4682B4"
            : selectedVariant === "modern-purple"
            ? "#805AD5"
            : selectedVariant === "modern-green"
            ? "#228B22"

            // ELITE (2nd) ✅ MOVED UP
            : selectedVariant === "elite-gray"
            ? "#787878"
            : selectedVariant === "elite-black"
            ? "#000000"
            : selectedVariant === "elite-emerald"
            ? "#008C64"

            // EXECUTIVE (3rd / LAST) ✅ MOVED DOWN
            : selectedVariant === "executive-gold"
            ? "#B4963C"
            : selectedVariant === "executive-navy"
            ? "#193264"
            : selectedVariant === "executive-charcoal"
            ? "#505050"

            : "#999",
            }}
            />
            </div>


            <button
             onClick={() => {
             if (!loading) handleGenerate();
             }}
             disabled={loading}
             className="primary-btn generate-btn"
            >
              {loading ? "Generating..." : "Generate Resume"}
            </button>
            </div>

          {/* RESUME PREVIEW */}
          {resume && (
            <div className="resume-preview">
              <h3 className="preview-title">AI Generated Resume</h3>
              <pre className="resume-box">{resume}</pre>

              <p
                style={{
                  fontSize: "14px",
                  color: "#666",
                  marginTop: "12px",
                  marginBottom: "10px",
                  textAlign: "center",
                  fontWeight: "500",
                }}
              >
                ✨ Download the styled professional PDF version below.
              </p>

              <button
                className="download-btn"
                onClick={handleDownloadPDF}
                style={{
                  display: "block",
                  margin: "0 auto",
                }}
              >
                Download PDF Styled Professional Resume{" "}
                <span style={{ opacity: 0.7 }}>(Pro)</span>
              </button>
            </div>
          )}



        </div>
                </div> {/* end right column */}

    </div> {/* end dashboard-layout */}

        {/* FOOTER */}
        <footer className="footer">
          © 2026 EmployPilot. All Rights Reserved.
          <span className="footer-separator"> | </span>
          <Link to="/terms" className="footer-link">
            Terms of Use
          </Link>
          <span className="footer-separator"> | </span>
          <Link to="/privacy" className="footer-link">
            Privacy Policy
          </Link>
          <span className="footer-separator"> | </span>
          <Link to="/contact" className="footer-link">
            Contact
          </Link>
        </footer>

{/* PAYWALL MODAL */}
{showPaywall && (
  <div
    className="paywall-overlay"
    onClick={() => {
      paywallRequestedRef.current = false;
      setShowPaywall(false);
    }}
  >
    <div
      className="paywall-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="paywall-title">Unlock PDF Downloads</h3>

      <p className="paywall-desc">
        Upgrade to EmployPilot Pro to unlock unlimited PDF downloads,
        premium templates, AI cover letters, interview prep tools,
        and access to the Career Lounge.
      </p>

      <div className="paywall-buttons">
        <a href="/pricing" className="paywall-btn-primary">
          Upgrade to Pro
        </a>

        <button
          className="paywall-btn-secondary"
          onClick={() => {
            paywallRequestedRef.current = false;
            setShowPaywall(false);
          }}
        >
          Not Now
        </button>
      </div>
    </div>
  </div>
)}
</div>

      
    </>
  );
}

export default Dashboard;



